import { forwardRef, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiErrorMessage } from "@/lib/api-error";
import { invoicePdfTemplateValues, type components } from "@dolfin/api-public";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { CheckIcon, ChevronRight, ChevronsUpDownIcon, CopyIcon } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ReminderOffsetMultiSelect } from "@/components/ReminderOffsetMultiSelect";
import { usePermissions } from "@/lib/permissions";
import { PermissionGuard } from "@/components/PermissionGuard";
import { LoadingState } from "@/components/PageUi";
import { InvoicePdfPreview } from "@/components/InvoicePdfPreview";

type OrganisationResponse = components["schemas"]["OrganisationResponse"];
type OrganisationArSettingsResponse = components["schemas"]["OrganisationArSettingsResponse"];
type IndustryResponse = components["schemas"]["IndustryResponse"];
type CountrySummary = components["schemas"]["CountryBankProfileSummary"];
type CountryProfile = components["schemas"]["CountryBankProfile"];
type CurrencyResponse = components["schemas"]["CurrencyResponse"];
type InvoiceReminderOffset = components["schemas"]["InvoiceReminderOffset"];

const DIRTY_RING = "ring-2 ring-amber-500/50";

const FormSection = forwardRef<HTMLDivElement, { title: string; children: ReactNode }>(function FormSection(
  { title, children },
  ref,
) {
  return (
    <div ref={ref} className="scroll-mt-4 rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
});

const bankProfileKeys = [
  "accountHolderName",
  "accountNumber",
  "sortCode",
  "routingNumber",
  "iban",
  "bicSwift",
] as const;

function isBankFieldVisible(profile: CountryProfile | null, key: typeof bankProfileKeys[number]): boolean {
  if (!profile) return true;
  const spec = profile[key];
  if (!spec) return key === "accountHolderName";
  return spec.requirement !== "Hidden";
}

function isBankFieldRequired(profile: CountryProfile | null, key: typeof bankProfileKeys[number]): boolean {
  return profile?.[key]?.requirement === "Required";
}

function labelFrom(map: Record<string, string>, value: string): string {
  return map[value] ?? value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

const businessTypeLabels: Record<string, string> = {
  SoleTrader: "Sole Trader",
  LimitedCompany: "Limited Company",
  LLP: "LLP",
  Partnership: "Partnership",
};

const invoiceMessageToneOptions: { value: string; label: string; description: string }[] = [
  { value: "Friendly", label: "Friendly", description: "Warm and conversational." },
  { value: "Direct", label: "Direct", description: "Brief and to the point." },
  { value: "Formal", label: "Formal", description: "Reserved and businesslike." },
];

const employeeCountLabels: Record<string, string> = {
  OneToTen: "1\u201310",
  ElevenToFifty: "11\u201350",
  FiftyOneToTwoHundred: "51\u2013200",
  TwoHundredToOneThousand: "200\u20131,000",
  OneThousandPlus: "1,000+",
};

const annualRevenueLabels: Record<string, string> = {
  Under100k: "Under \u00a3100k",
  OneHundredToFiveHundredK: "\u00a3100k\u2013\u00a3500k",
  FiveHundredKToTwoM: "\u00a3500k\u2013\u00a32M",
  TwoMPlus: "\u00a32M+",
};

const avgInvoicesPerMonthLabels: Record<string, string> = {
  OneToTen: "1\u201310",
  ElevenToFifty: "11\u201350",
  FiftyToOneHundred: "50\u2013100",
  OneHundredPlus: "100+",
};

const avgInvoiceValueLabels: Record<string, string> = {
  Under500: "Under \u00a3500",
  FiveHundredToFiveK: "\u00a3500\u2013\u00a35k",
  FiveKToFiftyK: "\u00a35k\u2013\u00a350k",
  Over50k: "Over \u00a350k",
};

const primaryPaymentMethodLabels: Record<string, string> = {
  BankTransfer: "Bank Transfer",
  Card: "Card",
  DirectDebit: "Direct Debit",
  Cash: "Cash",
};

const baseFields = {
  name: z.string().min(1, "Organisation name is required"),
  contactEmail: z.string().min(1, "Contact email is required").email("Invalid email"),
  phone: z.string(),
  website: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string(),
  city: z.string(),
  postalCode: z.string(),
  country: z.string().min(1, "Country is required").max(2),
  taxNumber: z.string(),
  companyRegistrationNumber: z.string(),
  vatRegistered: z.string(),
  businessType: z.string(),
  incorporationYear: z.string(),
  employeeCount: z.string(),
  annualRevenue: z.string(),
  avgInvoicesPerMonth: z.string(),
  avgInvoiceValue: z.string(),
  primaryPaymentMethod: z.string(),
  primaryColour: z.string().max(7),
  logoUrl: z.string(),
  defaultCurrency: z.string().max(3),
  defaultPaymentTermsDays: z.string(),
  latePaymentFee: z.string(),
  defaultInvoiceMessageTone: z.string(),
  defaultReminderOffsets: z.array(
    z.enum(["OneWeekBefore", "TwoDaysBefore", "OnDueDate", "TwoDaysAfter", "OneWeekAfter"]),
  ),
  // Comma / newline separated in the form; split into an array on save.
  defaultInvoiceCcEmails: z.string(),
  defaultReplyToEmail: z.string(),
  invoiceNumberPrefix: z.string(),
  invoiceNumberPadding: z.string(),
  creditNoteNumberPrefix: z.string(),
  creditNoteNumberPadding: z.string(),
  invoicePdfTemplate: z.enum(["Classic", "Minimal", "Branded"]),
};

const bankBaseFields = {
  bankAccountHolderName: z.string(),
  bankAccountNumber: z.string(),
  bankSortCode: z.string(),
  bankRoutingNumber: z.string(),
  bankIban: z.string(),
  bankBicSwift: z.string(),
  bankCountry: z.string().max(2),
  bankCurrency: z.string().max(3),
};

const orgSettingsSchema = z.object({
  ...baseFields,
  ...bankBaseFields,
});

const orgSettingsOnboardingSchema = z.object({
  ...baseFields,
  ...bankBaseFields,
  bankAccountHolderName: z.string().min(1, "Account holder name is required"),
  bankCountry: z.string().min(1, "Bank country is required").max(2),
  bankCurrency: z.string().min(1, "Bank currency is required").max(3),
});

const orgSettingsApOnboardingSchema = z.object({
  ...baseFields,
  ...bankBaseFields,
  name: z.string().min(1, "Organisation name is required"),
  contactEmail: z.string().min(1, "Contact email is required").email("Invalid email"),
  country: z.string().min(1, "Country is required").max(2),
  businessType: z.string().min(1, "Business type is required"),
  taxNumber: z.string().min(1, "VAT number is required"),
  companyRegistrationNumber: z.string().min(1, "Company registration number is required"),
  addressLine1: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  postalCode: z.string().min(1, "Postal code is required"),
  defaultPaymentTermsDays: z.string().min(1, "Payment terms are required"),
  bankAccountHolderName: z.string().min(1, "Account holder name is required"),
  bankCountry: z.string().min(1, "Bank country is required").max(2),
  bankCurrency: z.string().min(1, "Bank currency is required").max(3),
});

type OrgSettingsFormValues = z.infer<typeof orgSettingsSchema>;
// "ar-settings" renders only the AR sections and saves solely to the AR settings endpoint.
type FormMode = "default" | "ar-onboarding" | "ap-onboarding" | "ar-settings";

const schemaByMode: Record<FormMode, z.ZodType<OrgSettingsFormValues>> = {
  default: orgSettingsSchema,
  "ar-onboarding": orgSettingsOnboardingSchema,
  "ap-onboarding": orgSettingsApOnboardingSchema,
  "ar-settings": orgSettingsSchema,
};

function isArSettingsOnly(mode: FormMode) {
  return mode === "ar-settings";
}

const requiredFieldsByMode: Record<FormMode, Set<string>> = {
  default: new Set(["name", "contactEmail", "country"]),
  "ar-settings": new Set([]),
  "ar-onboarding": new Set([
    "name", "contactEmail", "country",
    "bankAccountHolderName", "bankCountry", "bankCurrency",
  ]),
  "ap-onboarding": new Set([
    "name", "contactEmail", "country", "businessType", "industry",
    "taxNumber", "companyRegistrationNumber", "addressLine1", "city",
    "postalCode", "bankAccountHolderName", "bankCountry", "bankCurrency",
    "defaultPaymentTermsDays",
  ]),
};

// The AR-owned slice of the form values, sourced from the org AR settings response. Used both by
// toFormValues and to refresh just these fields after an AR-only save.
function arFieldsFrom(ar: OrganisationArSettingsResponse | null) {
  return {
    defaultPaymentTermsDays: ar?.defaultPaymentTermsDays?.toString() ?? "",
    latePaymentFee: ar?.latePaymentFee != null ? String(ar.latePaymentFee) : "",
    defaultInvoiceMessageTone: ar?.defaultInvoiceMessageTone ?? "Friendly",
    defaultReminderOffsets: ar?.defaultReminderOffsets ?? [],
    defaultInvoiceCcEmails: (ar?.defaultInvoiceCcEmails ?? []).join(", "),
    defaultReplyToEmail: ar?.defaultReplyToEmail ?? "",
    invoiceNumberPrefix: ar?.invoiceNumberPrefix ?? "INV",
    invoiceNumberPadding: ar?.invoiceNumberPadding != null ? String(ar.invoiceNumberPadding) : "8",
    creditNoteNumberPrefix: ar?.creditNoteNumberPrefix ?? "CN",
    creditNoteNumberPadding: ar?.creditNoteNumberPadding != null ? String(ar.creditNoteNumberPadding) : "8",
    invoicePdfTemplate: ar?.invoicePdfTemplate ?? "Classic",
    bankAccountHolderName: ar?.bankDetails?.accountHolderName ?? "",
    bankAccountNumber: ar?.bankDetails?.accountNumber ?? "",
    bankSortCode: ar?.bankDetails?.sortCode ?? "",
    bankRoutingNumber: ar?.bankDetails?.routingNumber ?? "",
    bankIban: ar?.bankDetails?.iban ?? "",
    bankBicSwift: ar?.bankDetails?.bicSwift ?? "",
    bankCountry: ar?.bankDetails?.country ?? "",
    bankCurrency: ar?.bankDetails?.currency ?? "",
  };
}

function toFormValues(
  org: OrganisationResponse,
  ar: OrganisationArSettingsResponse | null,
): OrgSettingsFormValues {
  return {
    name: org.name ?? "",
    contactEmail: org.contactEmail ?? "",
    phone: org.phone ?? "",
    website: org.website ?? "",
    addressLine1: org.addressLine1 ?? "",
    addressLine2: org.addressLine2 ?? "",
    city: org.city ?? "",
    postalCode: org.postalCode ?? "",
    country: org.country ?? "",
    taxNumber: org.taxNumber ?? "",
    companyRegistrationNumber: org.companyRegistrationNumber ?? "",
    vatRegistered: org.vatRegistered != null ? String(org.vatRegistered) : "",
    businessType: org.businessType ?? "",
    incorporationYear: org.incorporationYear != null ? String(org.incorporationYear) : "",
    employeeCount: org.employeeCount ?? "",
    annualRevenue: org.annualRevenue ?? "",
    avgInvoicesPerMonth: org.avgInvoicesPerMonth ?? "",
    avgInvoiceValue: org.avgInvoiceValue ?? "",
    primaryPaymentMethod: org.primaryPaymentMethod ?? "",
    primaryColour: org.primaryColour ?? "",
    logoUrl: org.logoUrl ?? "",
    // DefaultCurrency stays on the organisation (functional/reporting currency); the rest live on the AR settings.
    defaultCurrency: org.defaultCurrency ?? "",
    ...arFieldsFrom(ar),
  };
}

interface OrganisationSettingsFormProps {
  mode?: FormMode;
  scrollToBankDetails?: boolean;
  onSaveSuccess?: () => void;
}

export function OrganisationSettingsForm({
  mode = "default",
  scrollToBankDetails = false,
  onSaveSuccess,
}: OrganisationSettingsFormProps) {
  const api = useDolfinClient();
  const { organisationId: activeOrganisationId } = useDolfinAuth();
  const { can, demoReason } = usePermissions();
  // Onboarding flows (ap/ar) establish the org and aren't a "change org
  // settings" action, so only gate the live settings surface (default mode).
  const canChangeOrgSettings = mode !== "default" || can("changeOrgSettings");
  const bankDetailsRef = useRef<HTMLDivElement>(null);
  const requiredFields = requiredFieldsByMode[mode];
  const arOnly = isArSettingsOnly(mode);
  // The Organisation details page shows only identity + default currency; AR sections and the AR PATCH are skipped there.
  const showArSections = mode !== "default";
  // Backend-owned counter (not an editable field). Kept only to preview the next number to the user.
  const [arNextValue, setArNextValue] = useState<number | null>(null);
  const [arCreditNoteNextValue, setArCreditNoteNextValue] = useState<number | null>(null);
  const mark = (field: string) =>
    requiredFields.has(field) ? <span className="text-destructive ml-0.5">*</span> : null;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<IndustryResponse | null>(null);
  const [initialIndustryId, setInitialIndustryId] = useState<string | null>(null);
  // The industry picker sits outside react-hook-form, so Discard needs the loaded value to put back.
  const initialIndustry = useRef<IndustryResponse | null>(null);
  const [industryPickerOpen, setIndustryPickerOpen] = useState(false);
  const [industries, setIndustries] = useState<IndustryResponse[]>([]);
  const industriesFetched = useRef(false);
  const [industrySearch, setIndustrySearch] = useState("");
  const [expandedIndustryGroup, setExpandedIndustryGroup] = useState<string | null>(null);
  const [supportedCountries, setSupportedCountries] = useState<CountrySummary[]>([]);
  const countriesFetched = useRef(false);
  const [availableCurrencies, setAvailableCurrencies] = useState<CurrencyResponse[]>([]);
  const currenciesFetched = useRef(false);
  const [bankProfile, setBankProfile] = useState<CountryProfile | null>(null);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [bankCountryPickerOpen, setBankCountryPickerOpen] = useState(false);
  const [bankCurrencyPickerOpen, setBankCurrencyPickerOpen] = useState(false);
  const [defaultCurrencyPickerOpen, setDefaultCurrencyPickerOpen] = useState(false);

  const form = useForm<OrgSettingsFormValues>({
    resolver: zodResolver(schemaByMode[mode] as typeof orgSettingsSchema),
    defaultValues: {
      name: "",
      contactEmail: "",
      phone: "",
      website: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      postalCode: "",
      country: "",
      taxNumber: "",
      companyRegistrationNumber: "",
      vatRegistered: "",
      businessType: "",
      incorporationYear: "",
      employeeCount: "",
      annualRevenue: "",
      avgInvoicesPerMonth: "",
      avgInvoiceValue: "",
      primaryPaymentMethod: "",
      primaryColour: "",
      logoUrl: "",
      defaultCurrency: "",
      defaultPaymentTermsDays: "",
      latePaymentFee: "",
      defaultInvoiceMessageTone: "Friendly",
      defaultReminderOffsets: [],
      defaultInvoiceCcEmails: "",
      defaultReplyToEmail: "",
      invoiceNumberPrefix: "INV",
      invoiceNumberPadding: "8",
      creditNoteNumberPrefix: "CN",
      creditNoteNumberPadding: "8",
      invoicePdfTemplate: "Classic",
      bankAccountHolderName: "",
      bankAccountNumber: "",
      bankSortCode: "",
      bankRoutingNumber: "",
      bankIban: "",
      bankBicSwift: "",
      bankCountry: "",
      bankCurrency: "",
    },
  });

  const primaryColour = form.watch("primaryColour");

  // Live preview of the next invoice number from the (read-only) counter + the edited prefix/padding.
  const watchedPrefix = form.watch("invoiceNumberPrefix");
  const watchedPadding = form.watch("invoiceNumberPadding");
  const nextInvoiceNumberPreview = (() => {
    const seq = arNextValue ?? 1;
    const width = Math.min(10, Math.max(1, parseInt(watchedPadding || "8", 10) || 8));
    return `${watchedPrefix?.trim() || "INV"}-${String(seq).padStart(width, "0")}`;
  })();

  const watchedCnPrefix = form.watch("creditNoteNumberPrefix");
  const watchedCnPadding = form.watch("creditNoteNumberPadding");
  const nextCreditNoteNumberPreview = (() => {
    const seq = arCreditNoteNextValue ?? 1;
    const width = Math.min(10, Math.max(1, parseInt(watchedCnPadding || "8", 10) || 8));
    return `${watchedCnPrefix?.trim() || "CN"}-${String(seq).padStart(width, "0")}`;
  })();

  const groupedIndustries = useMemo(() => {
    const map = new Map<string, IndustryResponse[]>();
    for (const industry of industries) {
      const key = industry.group ?? "Other";
      const list = map.get(key);
      if (list) list.push(industry);
      else map.set(key, [industry]);
    }
    return Array.from(map.entries())
      .map(([group, items]) => ({
        group,
        label: group
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2"),
        items,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [industries]);

  useEffect(() => {
    if (industriesFetched.current) return;

    async function fetchIndustries() {
      const { data } = await api.GET("/v1/industries");
      if (data) setIndustries(data);
      industriesFetched.current = true;
    }

    fetchIndustries();
  }, []);

  useEffect(() => {
    if (countriesFetched.current) return;
    async function fetchCountries() {
      const { data } = await api.GET("/v1/bank-details/countries");
      if (data) setSupportedCountries(data);
      countriesFetched.current = true;
    }
    fetchCountries();
  }, []);

  useEffect(() => {
    if (currenciesFetched.current) return;
    async function fetchCurrencies() {
      const { data } = await api.GET("/v1/currencies/available");
      if (data) setAvailableCurrencies(data);
      currenciesFetched.current = true;
    }
    fetchCurrencies();
  }, []);

  const watchedBankCountry = form.watch("bankCountry");
  useEffect(() => {
    if (!watchedBankCountry) {
      setBankProfile(null);
      return;
    }
    let cancelled = false;
    async function fetchProfile() {
      const { data } = await api.GET("/v1/bank-details/countries/{code}/profile", {
        params: { path: { code: watchedBankCountry } },
      });
      if (!cancelled) setBankProfile(data ?? null);
    }
    fetchProfile();
    return () => { cancelled = true; };
  }, [watchedBankCountry]);

  // Pre-populate bank currency on country change when empty.
  useEffect(() => {
    if (bankProfile && !form.getValues("bankCurrency") && bankProfile.defaultCurrencies.length > 0) {
      form.setValue("bankCurrency", bankProfile.defaultCurrencies[0]!);
    }
  }, [bankProfile, form]);

  useEffect(() => {
    if (!activeOrganisationId) return;
    let cancelled = false;

    async function fetchOrg() {
      setLoading(true);
      const [orgResult, arResult] = await Promise.all([
        api.GET("/v1/organisations/{id}", { params: { path: { id: activeOrganisationId! } } }),
        api.GET("/v1/organisations/{organisationId}/settings/ar", {
          params: { path: { organisationId: activeOrganisationId! } },
        }),
      ]);
      const data = orgResult.data;
      if (cancelled || !data) {
        setLoading(false);
        return;
      }
      setArNextValue(Number(arResult.data?.invoiceNumberNextValue ?? 1));
      setArCreditNoteNextValue(Number(arResult.data?.creditNoteNumberNextValue ?? 1));
      form.reset(toFormValues(data, arResult.data ?? null));
      initialIndustry.current = data.industryId
        ? { id: data.industryId, code: data.industryCode ?? "", displayName: data.industryDisplayName ?? "", slug: "" }
        : null;
      setSelectedIndustry(initialIndustry.current);
      setInitialIndustryId(data.industryId ?? null);
      setLoading(false);
    }

    fetchOrg();
    return () => { cancelled = true; };
  }, [activeOrganisationId]);

  useEffect(() => {
    if (scrollToBankDetails && !loading && bankDetailsRef.current) {
      bankDetailsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [scrollToBankDetails, loading]);

  // Maps the RFC 9110 `errors` dict (bank validation) back to per-field form errors.
  function applyBankFieldErrors(apiError: unknown): boolean {
    const rfcErrors = (apiError as { errors?: Record<string, string[]> }).errors;
    if (rfcErrors && typeof rfcErrors === "object" && !Array.isArray(rfcErrors)) {
      const fieldMap: Record<string, keyof OrgSettingsFormValues> = {
        accountHolderName: "bankAccountHolderName",
        accountNumber: "bankAccountNumber",
        sortCode: "bankSortCode",
        routingNumber: "bankRoutingNumber",
        iban: "bankIban",
        bicSwift: "bankBicSwift",
        country: "bankCountry",
        currency: "bankCurrency",
      };
      let mapped = false;
      for (const [field, messages] of Object.entries(rfcErrors)) {
        const formField = fieldMap[field];
        if (formField) {
          form.setError(formField, { message: messages.join(" ") });
          mapped = true;
        }
      }
      if (mapped) {
        toast.error("Some bank details are invalid.");
        return true;
      }
    }
    return false;
  }

  function arSettingsBody(values: OrgSettingsFormValues) {
    return {
      defaultPaymentTermsDays: values.defaultPaymentTermsDays
        ? parseInt(values.defaultPaymentTermsDays, 10)
        : null,
      latePaymentFee: values.latePaymentFee ? parseFloat(values.latePaymentFee) : null,
      defaultInvoiceMessageTone:
        (values.defaultInvoiceMessageTone as "Friendly" | "Direct" | "Formal") || null,
      defaultReminderOffsets: values.defaultReminderOffsets,
      defaultInvoiceCcEmails: values.defaultInvoiceCcEmails
        .split(/[,\n]/)
        .map((e) => e.trim())
        .filter((e) => e.length > 0),
      defaultReplyToEmail: values.defaultReplyToEmail.trim(),
      invoiceNumberPrefix: values.invoiceNumberPrefix.trim() || "INV",
      invoiceNumberPadding: values.invoiceNumberPadding ? parseInt(values.invoiceNumberPadding, 10) : 8,
      creditNoteNumberPrefix: values.creditNoteNumberPrefix.trim() || "CN",
      creditNoteNumberPadding: values.creditNoteNumberPadding ? parseInt(values.creditNoteNumberPadding, 10) : 8,
      invoicePdfTemplate: values.invoicePdfTemplate,
      bankDetails: {
        accountHolderName: values.bankAccountHolderName.trim() || null,
        accountNumber: values.bankAccountNumber.trim() || null,
        sortCode: values.bankSortCode.replace(/\D/g, "") || null,
        routingNumber: values.bankRoutingNumber.replace(/\D/g, "") || null,
        iban: values.bankIban.trim().toUpperCase() || null,
        bicSwift: values.bankBicSwift.trim().toUpperCase() || null,
        country: values.bankCountry.trim().toUpperCase() || null,
        currency: values.bankCurrency.trim().toUpperCase() || null,
      },
    };
  }

  async function onSubmit(values: OrgSettingsFormValues) {
    if (!activeOrganisationId) return;

    const arOnly = isArSettingsOnly(mode);
    const persistAr = mode !== "default";

    if (!arOnly && !selectedIndustry?.id) {
      toast.error("Please select an industry.");
      return;
    }
    setSubmitting(true);

    if (persistAr) {
      const arResult = await api.PATCH("/v1/organisations/{organisationId}/settings/ar", {
        params: { path: { organisationId: activeOrganisationId } },
        body: arSettingsBody(values),
      });
      if (arResult.error) {
        if (!applyBankFieldErrors(arResult.error)) {
          toast.error(apiErrorMessage(arResult.error, "Failed to update AR settings."));
        }
        setSubmitting(false);
        return;
      }
    }

    let orgData: OrganisationResponse | null = null;
    if (!arOnly) {
      const { data, error: apiError } = await api.PUT("/v1/organisations/{id}", {
        params: { path: { id: activeOrganisationId } },
        body: {
          name: values.name.trim(),
          industryId: selectedIndustry!.id!,
          contactEmail: values.contactEmail.trim(),
          phone: values.phone.trim() || null,
          website: values.website.trim() || null,
          addressLine1: values.addressLine1.trim() || null,
          addressLine2: values.addressLine2.trim() || null,
          city: values.city.trim() || null,
          postalCode: values.postalCode.trim() || null,
          country: values.country.trim().toUpperCase(),
          taxNumber: values.taxNumber.trim() || null,
          companyRegistrationNumber: values.companyRegistrationNumber.trim() || null,
          vatRegistered: values.vatRegistered === "true" ? true : values.vatRegistered === "false" ? false : null,
          businessType: (values.businessType as "SoleTrader" | "LimitedCompany" | "LLP" | "Partnership") || null,
          incorporationYear: values.incorporationYear ? parseInt(values.incorporationYear, 10) : null,
          employeeCount: (values.employeeCount as "OneToTen" | "ElevenToFifty" | "FiftyOneToTwoHundred" | "TwoHundredToOneThousand" | "OneThousandPlus") || null,
          annualRevenue: (values.annualRevenue as "Under100k" | "OneHundredToFiveHundredK" | "FiveHundredKToTwoM" | "TwoMPlus") || null,
          avgInvoicesPerMonth: (values.avgInvoicesPerMonth as "OneToTen" | "ElevenToFifty" | "FiftyToOneHundred" | "OneHundredPlus") || null,
          avgInvoiceValue: (values.avgInvoiceValue as "Under500" | "FiveHundredToFiveK" | "FiveKToFiftyK" | "Over50k") || null,
          primaryPaymentMethod: (values.primaryPaymentMethod as "BankTransfer" | "Card" | "DirectDebit" | "Cash") || null,
          primaryColour: values.primaryColour.trim() || null,
          logoUrl: values.logoUrl.trim() || null,
          defaultCurrency: values.defaultCurrency.trim().toUpperCase() || null,
        },
      });

      if (apiError) {
        toast.error(apiErrorMessage(apiError, "Failed to update organisation."));
        setSubmitting(false);
        return;
      }
      orgData = data ?? null;
    }

    // Re-read AR settings so the form reflects any server canonicalisation (e.g. sort-code formatting).
    const refreshedAr = await api.GET("/v1/organisations/{organisationId}/settings/ar", {
      params: { path: { organisationId: activeOrganisationId } },
    });
    if (refreshedAr.data) {
      setArNextValue(Number(refreshedAr.data.invoiceNumberNextValue));
      setArCreditNoteNextValue(Number(refreshedAr.data.creditNoteNumberNextValue));
    }

    if (orgData) {
      form.reset(toFormValues(orgData, refreshedAr.data ?? null));
      initialIndustry.current = orgData.industryId
        ? { id: orgData.industryId, code: orgData.industryCode ?? "", displayName: orgData.industryDisplayName ?? "", slug: "" }
        : null;
      setSelectedIndustry(initialIndustry.current);
      setInitialIndustryId(orgData.industryId ?? null);
    } else if (refreshedAr.data) {
      // AR-only save: keep identity values already in the form, refresh just the AR fields.
      form.reset({ ...form.getValues(), ...arFieldsFrom(refreshedAr.data) });
    }
    toast.success(arOnly ? "AR settings updated." : "Organisation updated.");
    setSubmitting(false);
    onSaveSuccess?.();
  }

  if (loading) {
    return <LoadingState label="Loading organisation..." />;
  }

  const industryChanged = !arOnly && (selectedIndustry?.id ?? null) !== initialIndustryId;
  const dirty = form.formState.isDirty || industryChanged;
  // The standalone settings pages are long, so the save bar follows the reader down the page. Inside the
  // onboarding dialogs the form is short and the dialog owns the footer, so it stays where it was.
  const stickyFooter = mode === "default" || arOnly;

  function discard() {
    form.reset();
    setSelectedIndustry(initialIndustry.current);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {!arOnly && (
        <>
        <FormSection title="Business info">
            {mode === "default" && (
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none">Organisation ID</label>
                <div className="flex items-center gap-2">
                  <Input value={activeOrganisationId ?? ""} disabled className="flex-1 font-mono text-sm" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      if (activeOrganisationId) {
                        navigator.clipboard.writeText(activeOrganisationId);
                        toast.success("Organisation ID copied to clipboard");
                      }
                    }}
                  >
                    <CopyIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Organisation Name{mark("name")}</FormLabel>
                  <FormControl>
                    <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="businessType"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Business Type{mark("businessType")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className={cn("w-full", fieldState.isDirty && DIRTY_RING)}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.keys(businessTypeLabels).map((v) => (
                          <SelectItem key={v} value={v}>{labelFrom(businessTypeLabels, v)}</SelectItem>
                        ))}
                        {field.value && !businessTypeLabels[field.value] && (
                          <SelectItem value={field.value}>{labelFrom(businessTypeLabels, field.value)}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="incorporationYear"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Incorporation Year</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="2020" {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">Industry{mark("industry")}</label>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={industryPickerOpen}
                className={cn(
                  "w-full justify-between font-normal",
                  (selectedIndustry?.id ?? null) !== initialIndustryId && DIRTY_RING,
                )}
                onClick={() => {
                  const next = !industryPickerOpen;
                  setIndustryPickerOpen(next);
                  if (!next) {
                    setIndustrySearch("");
                    setExpandedIndustryGroup(null);
                  }
                }}
              >
                {selectedIndustry ? (
                  <span className="truncate">
                    {selectedIndustry.code} - {selectedIndustry.displayName}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select an industry...</span>
                )}
                <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>

              {industryPickerOpen && (
                <div className="rounded-md border">
                  <Command>
                    <CommandInput
                      placeholder="Search industries..."
                      value={industrySearch}
                      onValueChange={setIndustrySearch}
                    />
                    <CommandList>
                      <CommandEmpty>No industry found.</CommandEmpty>
                      {groupedIndustries.map(({ group, label, items }) => {
                        const isSearching = industrySearch.trim().length > 0;
                        const expanded = isSearching || expandedIndustryGroup === group;
                        return (
                          <CommandGroup key={group}>
                            {!isSearching && (
                              <CommandItem
                                value={`__group__ ${label}`}
                                onSelect={() =>
                                  setExpandedIndustryGroup(expanded ? null : group)
                                }
                              >
                                <ChevronRight
                                  className={cn(
                                    "mr-2 size-4 transition-transform",
                                    expanded && "rotate-90",
                                  )}
                                />
                                <span className="font-medium">{label}</span>
                                <span className="ml-auto text-xs text-muted-foreground">
                                  {items.length}
                                </span>
                              </CommandItem>
                            )}
                            {expanded &&
                              items.map((industry) => (
                                <CommandItem
                                  key={industry.id}
                                  value={`${industry.code} ${industry.displayName}`}
                                  onSelect={() => {
                                    setSelectedIndustry(industry);
                                    setIndustryPickerOpen(false);
                                    setIndustrySearch("");
                                    setExpandedIndustryGroup(null);
                                  }}
                                  className={!isSearching ? "pl-8" : ""}
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 size-4",
                                      selectedIndustry?.id === industry.id ? "opacity-100" : "opacity-0",
                                    )}
                                  />
                                  <span className="mr-2 font-mono text-xs text-muted-foreground">
                                    {industry.code}
                                  </span>
                                  {industry.displayName}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        );
                      })}
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="employeeCount"
              render={({ field, fieldState }) => (
                <FormItem className="w-1/2">
                  <FormLabel>Employee Count</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className={cn("w-full", fieldState.isDirty && DIRTY_RING)}>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.keys(employeeCountLabels).map((v) => (
                        <SelectItem key={v} value={v}>{labelFrom(employeeCountLabels, v)}</SelectItem>
                      ))}
                      {field.value && !employeeCountLabels[field.value] && (
                        <SelectItem value={field.value}>{labelFrom(employeeCountLabels, field.value)}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="contactEmail"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Contact Email{mark("contactEmail")}</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="website"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="vatRegistered"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>VAT Registered?</FormLabel>
                  <div className={cn("flex gap-2 rounded-md", fieldState.isDirty && DIRTY_RING)}>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "flex-1",
                        field.value === "true" && "border-accent-blue bg-accent-blue/10 text-accent-blue",
                      )}
                      onClick={() => field.onChange("true")}
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "flex-1",
                        field.value === "false" && "border-accent-blue bg-accent-blue/10 text-accent-blue",
                      )}
                      onClick={() => field.onChange("false")}
                    >
                      No
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="taxNumber"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>VAT Number{mark("taxNumber")}</FormLabel>
                    <FormControl>
                      <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyRegistrationNumber"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Company Reg. Number{mark("companyRegistrationNumber")}</FormLabel>
                    <FormControl>
                      <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {/* Default currency is org identity (functional/reporting currency), not an AR invoice default. */}
            <FormField
              control={form.control}
              name="defaultCurrency"
              render={({ field, fieldState }) => {
                const selected = availableCurrencies.find((c) => c.code === field.value);
                return (
                  <FormItem>
                    <FormLabel>Default Currency{mark("defaultCurrency")}</FormLabel>
                    <Popover open={defaultCurrencyPickerOpen} onOpenChange={setDefaultCurrencyPickerOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={defaultCurrencyPickerOpen}
                            className={cn("w-full justify-between font-normal", fieldState.isDirty && DIRTY_RING)}
                          >
                            {field.value ? (
                              <span className="truncate">
                                {selected ? `${selected.code} - ${selected.name}` : field.value}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Select a currency...</span>
                            )}
                            <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search currencies..." />
                          <CommandList>
                            <CommandEmpty>No currency found.</CommandEmpty>
                            <CommandGroup>
                              {availableCurrencies.map((c) => (
                                <CommandItem
                                  key={c.code}
                                  value={`${c.code} ${c.name}`}
                                  onSelect={() => {
                                    field.onChange(c.code);
                                    setDefaultCurrencyPickerOpen(false);
                                  }}
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 size-4",
                                      field.value === c.code ? "opacity-100" : "opacity-0",
                                    )}
                                  />
                                  <span className="mr-2 font-mono text-xs text-muted-foreground">
                                    {c.code}
                                  </span>
                                  {c.name}
                                  <span className="ml-auto text-muted-foreground">{c.symbol}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </FormSection>

        <FormSection title="Address">
            <FormField
              control={form.control}
              name="addressLine1"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Address Line 1{mark("addressLine1")}</FormLabel>
                  <FormControl>
                    <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="addressLine2"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Address Line 2</FormLabel>
                  <FormControl>
                    <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>City{mark("city")}</FormLabel>
                    <FormControl>
                      <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Postal Code{mark("postalCode")}</FormLabel>
                    <FormControl>
                      <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="country"
              render={({ field, fieldState }) => {
                const selected = supportedCountries.find((c) => c.countryCode === field.value);
                return (
                  <FormItem>
                    <FormLabel>Country{mark("country")}</FormLabel>
                    <Popover open={countryPickerOpen} onOpenChange={setCountryPickerOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={countryPickerOpen}
                            className={cn("w-full justify-between font-normal", fieldState.isDirty && DIRTY_RING)}
                          >
                            {field.value ? (
                              <span className="truncate">
                                {selected ? `${selected.countryCode} - ${selected.countryName}` : field.value}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">Select a country...</span>
                            )}
                            <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search countries..." />
                          <CommandList>
                            <CommandEmpty>No country found.</CommandEmpty>
                            <CommandGroup>
                              {supportedCountries.map((c) => (
                                <CommandItem
                                  key={c.countryCode}
                                  value={`${c.countryCode} ${c.countryName}`}
                                  onSelect={() => {
                                    field.onChange(c.countryCode);
                                    setCountryPickerOpen(false);
                                  }}
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 size-4",
                                      field.value === c.countryCode ? "opacity-100" : "opacity-0",
                                    )}
                                  />
                                  <span className="mr-2 font-mono text-xs text-muted-foreground">
                                    {c.countryCode}
                                  </span>
                                  {c.countryName}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </FormSection>

        <FormSection title="Financial profile">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="annualRevenue"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Approximate Annual Revenue</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className={cn("w-full", fieldState.isDirty && DIRTY_RING)}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.keys(annualRevenueLabels).map((v) => (
                          <SelectItem key={v} value={v}>{labelFrom(annualRevenueLabels, v)}</SelectItem>
                        ))}
                        {field.value && !annualRevenueLabels[field.value] && (
                          <SelectItem value={field.value}>{labelFrom(annualRevenueLabels, field.value)}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="avgInvoicesPerMonth"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Average Invoices per Month</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className={cn("w-full", fieldState.isDirty && DIRTY_RING)}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.keys(avgInvoicesPerMonthLabels).map((v) => (
                          <SelectItem key={v} value={v}>{labelFrom(avgInvoicesPerMonthLabels, v)}</SelectItem>
                        ))}
                        {field.value && !avgInvoicesPerMonthLabels[field.value] && (
                          <SelectItem value={field.value}>{labelFrom(avgInvoicesPerMonthLabels, field.value)}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="avgInvoiceValue"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Average Invoice Value</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className={cn("w-full", fieldState.isDirty && DIRTY_RING)}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.keys(avgInvoiceValueLabels).map((v) => (
                          <SelectItem key={v} value={v}>{labelFrom(avgInvoiceValueLabels, v)}</SelectItem>
                        ))}
                        {field.value && !avgInvoiceValueLabels[field.value] && (
                          <SelectItem value={field.value}>{labelFrom(avgInvoiceValueLabels, field.value)}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="primaryPaymentMethod"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Primary Payment Method</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className={cn("w-full", fieldState.isDirty && DIRTY_RING)}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.keys(primaryPaymentMethodLabels).map((v) => (
                          <SelectItem key={v} value={v}>{labelFrom(primaryPaymentMethodLabels, v)}</SelectItem>
                        ))}
                        {field.value && !primaryPaymentMethodLabels[field.value] && (
                          <SelectItem value={field.value}>{labelFrom(primaryPaymentMethodLabels, field.value)}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>

        <FormSection title="Branding">
            <FormField
              control={form.control}
              name="primaryColour"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Primary Colour</FormLabel>
                  <div className="flex items-center gap-3">
                    <FormControl>
                      <Input placeholder="#2DD4BF" maxLength={7} className={cn("flex-1", fieldState.isDirty && DIRTY_RING)} {...field} />
                    </FormControl>
                    <label
                      className="relative h-9 w-9 shrink-0 cursor-pointer rounded-md border"
                      style={{
                        backgroundColor:
                          primaryColour && /^#[0-9A-Fa-f]{6}$/.test(primaryColour)
                            ? primaryColour
                            : "#ffffff",
                      }}
                    >
                      <input
                        type="color"
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        value={
                          primaryColour && /^#[0-9A-Fa-f]{6}$/.test(primaryColour)
                            ? primaryColour
                            : "#ffffff"
                        }
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </label>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="logoUrl"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Logo URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
        </>
        )}

        {showArSections && (
        <>
        <FormSection title="Preferences">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="defaultPaymentTermsDays"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Payment Terms (days){mark("defaultPaymentTermsDays")}</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="30" {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="latePaymentFee"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Late Payment Fee (%)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="0.00" {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="defaultInvoiceMessageTone"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Invoice Email Tone</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className={cn("w-full", fieldState.isDirty && DIRTY_RING)}>
                        {/* Show only the bare label when the dropdown is closed; the description
                            sits in the SelectItem children for the open state only. */}
                        <SelectValue placeholder="Select tone...">
                          {invoiceMessageToneOptions.find((o) => o.value === field.value)?.label}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {invoiceMessageToneOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex flex-col">
                            <span>{opt.label}</span>
                            <span className="text-xs text-muted-foreground">{opt.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Default voice for invoice emails sent on your organisation's behalf. Can be overridden per send.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="defaultReminderOffsets"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Default Reminders</FormLabel>
                  <FormControl>
                    <ReminderOffsetMultiSelect
                      value={field.value as InvoiceReminderOffset[]}
                      onChange={(next) => field.onChange(next)}
                      dirty={fieldState.isDirty}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Reminder emails automatically scheduled when an invoice is sent, timed relative to the invoice's due date. Leave empty to disable auto-scheduling for this organisation. Can be overridden per send.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="defaultInvoiceCcEmails"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default CC recipients</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="accounts@example.com, bookkeeper@example.com"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Comma-separated. Added to every invoice email and its reminders, unless overridden on the invoice. Can be overridden per invoice.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="defaultReplyToEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reply-To email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="billing@example.com"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Where replies to invoice and reminder emails go. Leave blank to use your organisation's contact email.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

        {/* The PDF layout is an AR setting like the rest, but only the AR settings page offers it: the
            onboarding dialogs stay short and keep whatever layout is already saved. */}
        {arOnly && (
          <FormSection title="Invoice PDF">
            <FormField
              control={form.control}
              name="invoicePdfTemplate"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>PDF Layout</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className={cn("w-56", fieldState.isDirty && DIRTY_RING)}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {invoicePdfTemplateValues.map((template) => (
                        <SelectItem key={template} value={template}>{template}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Layout for new invoices. Existing invoices keep their layout. Email templates are separate.
                  </p>
                  <FormMessage />
                  <InvoicePdfPreview template={field.value} />
                </FormItem>
              )}
            />
          </FormSection>
        )}

        <FormSection title="Invoice numbering">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="invoiceNumberPrefix"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prefix</FormLabel>
                    <FormControl>
                      <Input placeholder="INV" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="invoiceNumberPadding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Digits</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} max={10} {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              New invoices are numbered automatically in a sequence Dolfin manages for you. Next invoice:{" "}
              <span className="font-mono">{nextInvoiceNumberPreview}</span>
            </p>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <FormField
                control={form.control}
                name="creditNoteNumberPrefix"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit note prefix</FormLabel>
                    <FormControl>
                      <Input placeholder="CN" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="creditNoteNumberPadding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit note digits</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} max={10} {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Credit notes have their own sequence. Next credit note:{" "}
              <span className="font-mono">{nextCreditNoteNumberPreview}</span>. Numbers can be at most 20
              characters, the limit for e-invoices.
            </p>
          </FormSection>

        <FormSection title="Bank details" ref={bankDetailsRef}>
            <FormField
              control={form.control}
              name="bankAccountHolderName"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Account Holder Name{mark("bankAccountHolderName")}</FormLabel>
                  <FormControl>
                    <Input {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="bankCountry"
                render={({ field, fieldState }) => {
                  const selected = supportedCountries.find((c) => c.countryCode === field.value);
                  return (
                    <FormItem>
                      <FormLabel>Country{mark("bankCountry")}</FormLabel>
                      <Popover open={bankCountryPickerOpen} onOpenChange={setBankCountryPickerOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              aria-expanded={bankCountryPickerOpen}
                              className={cn("w-full justify-between font-normal", fieldState.isDirty && DIRTY_RING)}
                            >
                              {field.value ? (
                                <span className="truncate">
                                  {selected ? `${selected.countryCode} - ${selected.countryName}` : field.value}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">Select a country...</span>
                              )}
                              <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search countries..." />
                            <CommandList>
                              <CommandEmpty>No country found.</CommandEmpty>
                              <CommandGroup>
                                {supportedCountries.map((c) => (
                                  <CommandItem
                                    key={c.countryCode}
                                    value={`${c.countryCode} ${c.countryName}`}
                                    onSelect={() => {
                                      field.onChange(c.countryCode);
                                      setBankCountryPickerOpen(false);
                                    }}
                                  >
                                    <CheckIcon
                                      className={cn(
                                        "mr-2 size-4",
                                        field.value === c.countryCode ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    <span className="mr-2 font-mono text-xs text-muted-foreground">
                                      {c.countryCode}
                                    </span>
                                    {c.countryName}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="bankCurrency"
                render={({ field, fieldState }) => {
                  const selected = availableCurrencies.find((c) => c.code === field.value);
                  return (
                    <FormItem>
                      <FormLabel>Currency{mark("bankCurrency")}</FormLabel>
                      <Popover open={bankCurrencyPickerOpen} onOpenChange={setBankCurrencyPickerOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              aria-expanded={bankCurrencyPickerOpen}
                              className={cn("w-full justify-between font-normal", fieldState.isDirty && DIRTY_RING)}
                            >
                              {field.value ? (
                                <span className="truncate">
                                  {selected ? `${selected.code} - ${selected.name}` : field.value}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  {bankProfile?.defaultCurrencies[0]
                                    ? `Select a currency (e.g. ${bankProfile.defaultCurrencies[0]})`
                                    : "Select a currency..."}
                                </span>
                              )}
                              <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search currencies..." />
                            <CommandList>
                              <CommandEmpty>No currency found.</CommandEmpty>
                              <CommandGroup>
                                {availableCurrencies.map((c) => (
                                  <CommandItem
                                    key={c.code}
                                    value={`${c.code} ${c.name}`}
                                    onSelect={() => {
                                      field.onChange(c.code);
                                      setBankCurrencyPickerOpen(false);
                                    }}
                                  >
                                    <CheckIcon
                                      className={cn(
                                        "mr-2 size-4",
                                        field.value === c.code ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    <span className="mr-2 font-mono text-xs text-muted-foreground">
                                      {c.code}
                                    </span>
                                    {c.name}
                                    <span className="ml-auto text-muted-foreground">{c.symbol}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
            {(isBankFieldVisible(bankProfile, "sortCode") || isBankFieldVisible(bankProfile, "routingNumber")) && (
              <div className="grid grid-cols-2 gap-4">
                {isBankFieldVisible(bankProfile, "sortCode") && (
                  <FormField
                    control={form.control}
                    name="bankSortCode"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{bankProfile?.sortCode?.label ?? "Sort Code"}{isBankFieldRequired(bankProfile, "sortCode") && " *"}</FormLabel>
                        <FormControl>
                          <Input placeholder={bankProfile?.sortCode?.placeholder ?? "00-00-00"} {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {isBankFieldVisible(bankProfile, "routingNumber") && (
                  <FormField
                    control={form.control}
                    name="bankRoutingNumber"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{bankProfile?.routingNumber?.label ?? "Routing Number"}{isBankFieldRequired(bankProfile, "routingNumber") && " *"}</FormLabel>
                        <FormControl>
                          <Input placeholder={bankProfile?.routingNumber?.placeholder ?? "9 digits"} {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}
            {isBankFieldVisible(bankProfile, "accountNumber") && (
              <FormField
                control={form.control}
                name="bankAccountNumber"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{bankProfile?.accountNumber?.label ?? "Account Number"}{isBankFieldRequired(bankProfile, "accountNumber") && " *"}</FormLabel>
                    <FormControl>
                      <Input placeholder={bankProfile?.accountNumber?.example ?? undefined} {...field} className={cn(fieldState.isDirty && DIRTY_RING)} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {(isBankFieldVisible(bankProfile, "iban") || isBankFieldVisible(bankProfile, "bicSwift")) && (
              <div className="grid grid-cols-2 gap-4">
                {isBankFieldVisible(bankProfile, "iban") && (
                  <FormField
                    control={form.control}
                    name="bankIban"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{bankProfile?.iban?.label ?? "IBAN"}{isBankFieldRequired(bankProfile, "iban") && " *"}</FormLabel>
                        <FormControl>
                          <Input placeholder={bankProfile?.iban?.example ?? "DE89..."} {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} className={cn(fieldState.isDirty && DIRTY_RING)} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {isBankFieldVisible(bankProfile, "bicSwift") && (
                  <FormField
                    control={form.control}
                    name="bankBicSwift"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{bankProfile?.bicSwift?.label ?? "BIC / SWIFT"}{isBankFieldRequired(bankProfile, "bicSwift") && " *"}</FormLabel>
                        <FormControl>
                          <Input placeholder={bankProfile?.bicSwift?.placeholder ?? "BARCGB22"} {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} className={cn(fieldState.isDirty && DIRTY_RING)} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}
          </FormSection>
        </>
        )}

        <div
          className={cn(
            "flex items-center justify-end gap-3",
            stickyFooter &&
              "sticky bottom-3 z-10 rounded-xl border bg-background/85 px-4 py-3 shadow-sm backdrop-blur",
          )}
        >
          {stickyFooter ? (
            dirty ? (
              <span className="mr-auto inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                <span aria-hidden className="size-1.5 rounded-full bg-current" /> Unsaved changes
              </span>
            ) : (
              <span className="mr-auto text-xs text-muted-foreground">No unsaved changes</span>
            )
          ) : null}
          {stickyFooter && dirty ? (
            <Button type="button" variant="ghost" onClick={discard} disabled={submitting}>
              Discard
            </Button>
          ) : null}
          <PermissionGuard
            allowed={canChangeOrgSettings}
            reason="Only owners and admins can change organisation settings"
            demoReason={demoReason("changeOrgSettings")}
          >
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save changes
            </Button>
          </PermissionGuard>
        </div>
      </form>
    </Form>
  );
}
